/* eslint-disable */
import React, { useEffect, useState, useMemo } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Stack,
  Textarea,
  useToast,
  HStack,
  NumberInput,
  NumberInputField,
  Spinner,
  Box,
  IconButton,
  VStack,
  Text,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { BASE_URL } from "../../services/api.js";

/**
 * Props:
 * - isOpen
 * - onClose
 * - posCounterId (optional) — prefills the field
 * - tenantId (required to fetch users/handlers)
 * - onCreated (createdTransaction)
 */
export default function CreateTransactionModal({
  isOpen,
  onClose,
  posCounterId = "",
  tenantId,
  onCreated,
}) {
  const toast = useToast();

  // metadata
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);

  // payments state: array of { id, paymentType, amount, transactionRef, instrument: {cardLast4, upiVpa} }
  const emptyPayment = () => ({
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    paymentType: "",
    amount: "",
    transactionRef: "",
    instrument: { cardLast4: "", upiVpa: "" },
  });

  const [payments, setPayments] = useState([emptyPayment()]);

  // top-level transaction metadata
  const [orderId, setOrderId] = useState("");
  const [posId, setPosId] = useState(posCounterId || "");
  const [handledBy, setHandledBy] = useState("");
  const [status, setStatus] = useState("SUCCESS");
  const [notes, setNotes] = useState("");

  // submit state
  const [submitting, setSubmitting] = useState(false);

  // when modal opens, load meta and initialize
  useEffect(() => {
    async function loadMeta() {
      setLoadingMeta(true);
      try {
        const ptRes = await fetch(`${BASE_URL}/paymentTypes/getPaymentTypes`);
        const ptJson = await ptRes.json();
        setPaymentTypes(
          Array.isArray(ptJson.data) ? ptJson.data : ptJson || [],
        );
      } catch (err) {
        console.error("Failed to load payment types", err);
        toast({
          title: "Failed to load payment types",
          status: "error",
          duration: 3000,
        });
      }

      try {
        if (tenantId) {
          const uRes = await fetch(
            `${BASE_URL}/novatraAuthRoutes/getUsers?tenantId=${tenantId}`,
          );
          const uJson = await uRes.json();
          setUsers(
            Array.isArray(uJson.data)
              ? uJson.data
              : Array.isArray(uJson)
                ? uJson
                : [],
          );
        } else {
          setUsers([]);
        }
      } catch (err) {
        console.error("Failed to load users", err);
        toast({
          title: "Failed to load users",
          status: "error",
          duration: 3000,
        });
      } finally {
        setLoadingMeta(false);
      }
    }

    if (isOpen) {
      // reset / initialize fields
      setPosId(posCounterId || "");
      setPayments([emptyPayment()]);
      setOrderId("");
      setHandledBy("");
      setStatus("SUCCESS");
      setNotes("");
      loadMeta();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tenantId, posCounterId]);

  // derived total
  const totalAmount = useMemo(() => {
    return payments.reduce((s, p) => {
      const n = Number(p.amount || 0);
      return s + (isNaN(n) ? 0 : n);
    }, 0);
  }, [payments]);

  // helpers to update payments
  const updatePayment = (id, key, value) => {
    setPayments((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [key]: value } : p)),
    );
  };

  const updatePaymentInstrument = (id, key, value) => {
    setPayments((prev) =>
      prev.map((p) =>
        p.id === id
          ? { ...p, instrument: { ...p.instrument, [key]: value } }
          : p,
      ),
    );
  };

  const addPayment = () => setPayments((prev) => [...prev, emptyPayment()]);

  const removePayment = (id) =>
    setPayments((prev) =>
      prev.length === 1 ? prev : prev.filter((p) => p.id !== id),
    );

  // validation and submit
  const handleSubmit = async () => {
    // top-level validations
    if (!orderId)
      return toast({ title: "Order ID is required", status: "warning" });
    if (!posId)
      return toast({ title: "posCounterId is required", status: "warning" });
    if (!handledBy)
      return toast({ title: "Select handledBy", status: "warning" });
    if (payments.length === 0)
      return toast({ title: "Add at least one payment", status: "warning" });

    // per-payment validation
    for (const [i, p] of payments.entries()) {
      if (!p.paymentType)
        return toast({
          title: `Payment #${i + 1}: select payment type`,
          status: "warning",
        });
      const amt = Number(p.amount);
      if (isNaN(amt) || amt <= 0)
        return toast({
          title: `Payment #${i + 1}: amount must be > 0`,
          status: "warning",
        });

      const pt = (p.paymentType || "").toUpperCase();
      if (pt === "CARD") {
        if (
          !p.instrument ||
          !p.instrument.cardLast4 ||
          p.instrument.cardLast4.toString().length !== 4
        ) {
          return toast({
            title: `Payment #${i + 1}: enter last 4 digits of card`,
            status: "warning",
          });
        }
      }
      if (pt === "UPI") {
        if (!p.instrument || !p.instrument.upiVpa) {
          return toast({
            title: `Payment #${i + 1}: enter UPI VPA`,
            status: "warning",
          });
        }
      }
    }

    // build payload: payments[] with canonical upper-case paymentType and instrument normalization
    const payloadPayments = payments.map((p) => ({
      paymentType: (p.paymentType || "").toUpperCase(),
      amount: Number(p.amount),
      transactionRef: p.transactionRef || null,
      instrument: {
        cardLast4: p.instrument?.cardLast4 || null,
        upiVpa: p.instrument?.upiVpa || null,
      },
    }));

    const payload = {
      orderId,
      payments: payloadPayments,
      totalAmount,
      posCounterId: posId,
      handledBy,
      status,
      notes: notes || null,
    };

    setSubmitting(true);
    try {
      const res = await fetch(
        `${BASE_URL}/api/transactions/createTransaction`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Server ${res.status}: ${text}`);
      }

      const json = await res.json();
      const created = json.data || json.item || json.transaction || json;

      toast({
        title: "Transaction created",
        status: "success",
        duration: 2500,
      });

      if (onCreated) onCreated(created);

      // reset and close
      setPayments([emptyPayment()]);
      setOrderId("");
      setHandledBy("");
      setNotes("");
      setStatus("SUCCESS");
      onClose();
    } catch (err) {
      console.error("create tx error:", err);
      toast({
        title: "Failed to create transaction",
        description: err.message || String(err),
        status: "error",
        duration: 4000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // paymentType options
  const paymentTypeOptions = paymentTypes.length
    ? paymentTypes.map((p) => {
        const name = (p.name || "").toUpperCase();
        return { id: p._id, display: p.name || name, value: name };
      })
    : ["CASH", "UPI", "CARD", "CREDIT"].map((v) => ({
        id: v,
        display: v,
        value: v,
      }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Transaction (Split payments)</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            {loadingMeta && (
              <Box>
                <Spinner size="sm" mr={2} /> Loading payment types & users...
              </Box>
            )}

            <FormControl isRequired>
              <FormLabel>Order ID</FormLabel>
              <Input
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="e.g. 64f9b34c2a8c123456789012"
              />
            </FormControl>

            <VStack align="stretch" spacing={3}>
              <HStack justify="space-between">
                <Text fontWeight="semibold">Payment lines</Text>
                <Button size="sm" leftIcon={<AddIcon />} onClick={addPayment}>
                  Add payment
                </Button>
              </HStack>

              {payments.map((p, idx) => (
                <Box key={p.id} p={3} borderWidth="1px" borderRadius="md">
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="semibold">
                      # {idx + 1}
                    </Text>
                    <IconButton
                      aria-label="Remove payment"
                      size="sm"
                      icon={<DeleteIcon />}
                      onClick={() => removePayment(p.id)}
                      isDisabled={payments.length === 1}
                    />
                  </HStack>

                  <HStack spacing={3} align="flex-end">
                    <FormControl isRequired>
                      <FormLabel>Payment Type</FormLabel>
                      <Select
                        placeholder="Select"
                        value={p.paymentType}
                        onChange={(e) =>
                          updatePayment(p.id, "paymentType", e.target.value)
                        }
                      >
                        {paymentTypeOptions.map((opt) => (
                          <option key={opt.id} value={opt.value}>
                            {opt.display}
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Amount</FormLabel>
                      <NumberInput
                        min={0}
                        precision={2}
                        value={p.amount}
                        onChange={(val) => updatePayment(p.id, "amount", val)}
                      >
                        <NumberInputField placeholder="0.00" />
                      </NumberInput>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Transaction Ref</FormLabel>
                      <Input
                        value={p.transactionRef}
                        onChange={(e) =>
                          updatePayment(p.id, "transactionRef", e.target.value)
                        }
                        placeholder="PSP ref (optional)"
                      />
                    </FormControl>
                  </HStack>

                  {/* conditional instrument inputs */}
                  {(p.paymentType || "").toUpperCase() === "CARD" && (
                    <FormControl mt={3} isRequired>
                      <FormLabel>Card last 4 digits</FormLabel>
                      <Input
                        maxLength={4}
                        value={p.instrument.cardLast4}
                        onChange={(e) =>
                          updatePaymentInstrument(
                            p.id,
                            "cardLast4",
                            e.target.value.replace(/\D/g, "").slice(0, 4),
                          )
                        }
                        placeholder="e.g. 4242"
                        width="200px"
                      />
                    </FormControl>
                  )}

                  {(p.paymentType || "").toUpperCase() === "UPI" && (
                    <FormControl mt={3} isRequired>
                      <FormLabel>UPI VPA</FormLabel>
                      <Input
                        value={p.instrument.upiVpa}
                        onChange={(e) =>
                          updatePaymentInstrument(
                            p.id,
                            "upiVpa",
                            e.target.value,
                          )
                        }
                        placeholder="customer@upi"
                        width="300px"
                      />
                    </FormControl>
                  )}
                </Box>
              ))}
            </VStack>

            <HStack justify="space-between" mt={2}>
              <Text fontWeight="bold">Total Amount</Text>
              <Text fontWeight="bold">₹ {totalAmount.toFixed(2)}</Text>
            </HStack>

            <HStack spacing={3}>
              <FormControl isRequired>
                <FormLabel>POS Counter ID</FormLabel>
                <Input
                  value={posId}
                  onChange={(e) => setPosId(e.target.value)}
                  placeholder="68c6eaae82df4b8bd73e78ae"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Handled By</FormLabel>
                <Select
                  value={handledBy}
                  onChange={(e) => setHandledBy(e.target.value)}
                  placeholder="Select handler"
                >
                  {users.length === 0 ? (
                    <option value="">No users</option>
                  ) : (
                    users.map((u) => (
                      <option key={u._id} value={u._id}>
                        {u.name || u.email}
                      </option>
                    ))
                  )}
                </Select>
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel>Status</FormLabel>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="PENDING">PENDING</option>
                <option value="SUCCESS">SUCCESS</option>
                <option value="FAILED">FAILED</option>
                <option value="REFUNDED">REFUNDED</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Notes</FormLabel>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes"
              />
            </FormControl>
          </Stack>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            isDisabled={submitting}
          >
            Cancel
          </Button>
          <Button
            colorScheme="teal"
            onClick={handleSubmit}
            isLoading={submitting}
          >
            Create Transaction
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
